import type { FormikValues } from 'formik';
import validator from 'validator';
import * as Yup from 'yup';

import Provision from '../../api/provision';
import { FormModal } from '../../components/Formik';
import useApiQuery from '../../hooks/useApiQuery';

import type { APIHookArg } from '@/types';
import type { Contact } from '@/types/contactTypes';

interface EditContactFormModalProps {
  contact: Contact;
  onSubmit: () => void;
  onCancel: () => void;
}

const EditContactFormModal = ({
  contact,
  onSubmit,
  onCancel,
}: EditContactFormModalProps): React.JSX.Element => {
  const { makeApiCall: updateContactCall } = useApiQuery(
    Provision.editContact as unknown as APIHookArg,
    {
      successMessage: 'contactEdited',
    }
  );

  const fields = [
    {
      fieldType: 'text',
      name: 'name',
      label: 'Name',
    },
    {
      fieldType: 'text',
      name: 'phoneNumber',
      label: 'Phone Number',
    },
    {
      fieldType: 'text',
      name: 'slackChannel',
      label: 'Slack Channel',
    },
  ];

  const initialValues: FormikValues = {
    name: contact.name,
    phoneNumber: contact.phoneNumber,
    slackChannel: contact.slackChannel,
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().ensure(),
    phoneNumber: Yup.string().test({
      message: 'Please input valid phone number prefixed with country code',
      test: (value: string | undefined) =>
        value !== undefined ? validator.isMobilePhone(value, 'any', { strictMode: true }) : true,
    }),
    slackChannel: Yup.string(),
  });

  const updateContact = async (formValues: FormikValues): Promise<void> => {
    onCancel();

    const data: unknown = await updateContactCall(
      {
        ...formValues,
        juiceGroup: contact.juiceGroup,
        email: contact.email,
        type: contact.type,
      },
      contact._id
    );

    if (data) {
      onSubmit();
    }
  };

  return (
    <FormModal
      title={`Edit contact ${contact.email}`}
      fields={fields}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={values => {
        void updateContact(values);
      }}
      onCancel={onCancel}
      submitLabel='Save'
    />
  );
};

export default EditContactFormModal;
